import React, { Component } from 'react';
import PropTypes from 'prop-types';
import coin from '../asset/img/coin.png';
import gem from '../asset/img/gem.png';
import cloud from '../asset/img/cloud.png';
import cursor from '../asset/img/cursor.png';
import cursorOpen from '../asset/img/cursor-open.png';
import cursorClose from '../asset/img/cursor-close.png';

const FPS = 60;
const FRAME_DELAY = 1000 / FPS;

class MoneyGame extends Component {
  constructor(props) {
    super(props);
    let scale = 1;
    if (window.innerWidth < 1000) scale = 0.8;
    if (window.innerWidth < 800) scale = 0.5;
    if (window.innerWidth < 500) scale = 0.3;
    this.state = {
      scale
    };
    this.canvasRef = React.createRef();
  }

  componentDidMount() {
    this.lastFrameTime = performance.now();

    this.fallingItems = [];
    this.cursorX = 0;
    this.cursorY = 0;

    this.coin = new Image();
    this.coin.src = coin;
    this.coinPool = [];

    this.gem = new Image();
    this.gem.src = gem;
    this.gemPool = [];

    this.cloud = new Image();
    this.cloud.src = cloud;

    this.cursor = new Image();
    this.cursor.src = cursor;

    this.cursorOpen = new Image();
    this.cursorOpen.src = cursorOpen;

    this.cursorClose = new Image();
    this.cursorClose.src = cursorClose;

    this.grabbingItem = 0;
    this.cloudDirections = [true, true, true];
    this.cloudHeights = [0, 0, 0];

    const canvas = this.canvasRef.current;
    this.ctx = canvas.getContext('2d');
    this.ctx.imageSmoothingEnabled = false;

    this.canvasWidth = canvas.width = 1000;
    this.canvasHeight = canvas.height = 500;

    // Start the game loop
    this.gameLoop(0);

    // Generate items every half second
    this.coinInterval = setInterval(() => {
      if (!document.hidden) {
        const seed = Math.random();
        if (seed > 0.4) {
          this.fallingItems.push(this.generateCoin());
        } else if (seed < 0.05) {
          this.fallingItems.push(this.generateGem());
        }
      }
    }, 250);

    // Set up mousemove event listener for pixel-perfect detection
    canvas.addEventListener('pointermove', this.handleMouseMove);
    canvas.addEventListener('touchstart', this.handleTouchMove);
    canvas.addEventListener('touchmove', this.handleTouchMove);
    window.addEventListener('resize', this.updateScale);
  }

  componentWillUnmount() {
    // Clean up the interval and mousemove listener on unmount
    clearInterval(this.coinInterval);
    const canvas = this.canvasRef.current;
    canvas.removeEventListener('pointermove', this.handleMouseMove);
    canvas.removeEventListener('touchstart', this.handleTouchMove);
    canvas.removeEventListener('touchmove', this.handleTouchMove);
    window.removeEventListener('resize', this.updateScale);
  }

  generateCoin = () => {
    let item = this.coinPool.pop() || {};
    Object.assign(item, {
      img: this.coin,
      x: Math.random() * this.canvasWidth,
      diffX: 0,
      y: -100,
      radius: 15 + Math.random() * 10,
      speed: 5 + Math.random() * 8,
      score: 1,
      sound: () => this.props.playCoin(0.2)
    });
    return item;
  };

  generateGem = () => {
    let item = this.gemPool.pop() || {};
    Object.assign(item, {
      img: this.gem,
      x: Math.random() * this.canvasWidth,
      y: -100,
      radius: 10 + Math.random() * 5,
      invert: Math.random() > 0.5,
      speed: 8 + Math.random() * 10,
      score: 50,
      sound: () => this.props.playGem(0.5),
      gem: true
    });
    return item;
  };

  drawCloud = (x, y) => {
    this.ctx.drawImage(this.cloud, x, y, 120, 69);
  };

  gameLoop = (timestamp) => {
    const delta = timestamp - this.lastFrameTime;
    if (delta >= FRAME_DELAY) {
      this.lastFrameTime = timestamp - (delta % FRAME_DELAY);
      let score = this.props.score;

      // Draw falling items as images
      this.fallingItems.forEach((item, index) => {
        const dx = this.cursorX - item.x + (item.diffX || 0);
        const dy = this.cursorY - item.y;
        const distance = Math.sqrt(dx * dx + dy * dy); // Pythagorean theorem

        // If the distance is smaller than the radius of the falling item, it's a hit
        if (distance < item.radius + 15) {
          score += item.score;
          this.grabbingItem = 8;
          item.sound();
          if (item.gem) this.gemPool.push(item);
          else this.coinPool.push(item);
          this.fallingItems.splice(index, 1);
        } else if (item.y >= this.canvasHeight) {
          this.fallingItems.splice(index, 1);
        }
      });

      if (score > this.props.score) this.props.setScore(score);

      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      this.fallingItems.forEach((item) => {
        if (item.diffX !== undefined) {
          if (Math.random() < 0.4) {
            item.directionX =
              Math.abs(item.diffX) > item.radius / 2
                ? !item.directionX
                : item.directionX;

            if (item.directionX) item.diffX++;
            else item.diffX--;
          }
        }
        this.ctx.drawImage(
          item.img,
          item.x - item.radius + (item.diffX || 0),
          item.y - item.radius + item.speed,
          item.radius * 2,
          item.radius * 2
        );
        item.y += item.speed;
      });

      let cursor = this.cursor;
      if (this.grabbingItem > 1) {
        cursor = this.cursorOpen;
        this.grabbingItem--;
      }
      if (this.grabbingItem < 0) {
        cursor = this.cursorClose;
        this.grabbingItem++;
      }
      if (this.grabbingItem === 1) this.grabbingItem = -12;
      this.ctx.drawImage(cursor, this.cursorX - 20, this.cursorY - 25, 40, 50);

      const highCloudHeight = -25 + this.cloudHeights[0];
      const midCloudHeight = -20 - this.cloudHeights[1];
      const lowCloudHeight = -15 + this.cloudHeights[2];

      let i = 0;
      while (i < 3) {
        if (Math.random() < 0.1) {
          this.cloudDirections[i] =
            Math.abs(this.cloudHeights[i]) > 2
              ? !this.cloudDirections[i]
              : this.cloudDirections[i];

          if (this.cloudDirections[i]) this.cloudHeights[i]++;
          else this.cloudHeights[i]--;
        }
        i++;
      }

      this.drawCloud(-50, highCloudHeight);
      this.drawCloud(250, highCloudHeight);
      this.drawCloud(550, highCloudHeight);
      this.drawCloud(850, highCloudHeight);

      this.drawCloud(100, midCloudHeight);
      this.drawCloud(400, midCloudHeight);
      this.drawCloud(700, midCloudHeight);

      this.drawCloud(25, lowCloudHeight);
      this.drawCloud(175, lowCloudHeight);
      this.drawCloud(325, lowCloudHeight);
      this.drawCloud(475, lowCloudHeight);
      this.drawCloud(625, lowCloudHeight);
      this.drawCloud(775, lowCloudHeight);
      this.drawCloud(925, lowCloudHeight);
    }

    requestAnimationFrame(this.gameLoop);
  };

  handleMouseMove = (e) => {
    e.preventDefault();
    this.cursorX = e.offsetX / this.state.scale;
    this.cursorY = e.offsetY / this.state.scale;
  };

  handleTouchMove = (e) => {
    e.preventDefault();
    const bounds = e.target.getBoundingClientRect();
    this.cursorX = (e.targetTouches[0].clientX - bounds.x) / this.state.scale;
    this.cursorY = (e.targetTouches[0].clientY - bounds.y) / this.state.scale;
  };

  updateScale = (e) => {
    let scale = 1;
    if (window.innerWidth < 1000) scale = 0.8;
    if (window.innerWidth < 800) scale = 0.5;
    if (window.innerWidth < 500) scale = 0.3;
    this.setState({ scale });
  };

  render() {
    return (
      <div className="game-wrapper pixel-corners-wrapper">
        <canvas ref={this.canvasRef} className="game pixel-corners" />
      </div>
    );
  }
}

MoneyGame.propTypes = {
  score: PropTypes.number.isRequired,
  setScore: PropTypes.func.isRequired,
  playCoin: PropTypes.func.isRequired,
  playGem: PropTypes.func.isRequired
};

export default MoneyGame;
